import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { contacts } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const EMPTY = { name: "", phone: "", email: "", province: "", budget: "", message: "" };

export const ContactForm = () => {
  const { t } = useLang();
  const f = t.contact.fields;
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error(t.contact.privacyError);
      return;
    }
    const subject = encodeURIComponent(`[Franchising Zen Home] ${form.name || ""}`);
    const body = encodeURIComponent(
      `${f.name}: ${form.name}\n${f.phone}: ${form.phone}\n${f.email}: ${form.email}\n${f.province}: ${form.province}\n${f.budget}: ${form.budget}\n\n${f.message}:\n${form.message}`
    );
    toast.success(t.contact.success);
    window.location.href = `mailto:${contacts.email}?subject=${subject}&body=${body}`;
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/25 py-3 text-[#FAFAF7] placeholder:text-white/45 focus:border-[#8B7355] focus:outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact" className="relative bg-[#1A1A1A] text-[#FAFAF7] py-24 md:py-36">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <Reveal>
              <p className="text-sm tracking-luxe uppercase text-[#8B7355] mb-6">{t.contact.kicker}</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                {t.contact.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/60 max-w-md leading-relaxed">{t.contact.subtitle}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-12 space-y-4">
                <a href={`tel:${contacts.phoneTel}`} className="block link-underline w-fit text-lg">
                  {contacts.phoneDisplay}
                </a>
                <a href={`mailto:${contacts.email}`} className="block link-underline w-fit text-lg text-white/80">
                  {contacts.email}
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <input data-testid="input-name" required value={form.name} onChange={set("name")} placeholder={f.name} className={inputCls} />
                <input data-testid="input-phone" required value={form.phone} onChange={set("phone")} placeholder={f.phone} className={inputCls} />
                <input data-testid="input-email" type="email" required value={form.email} onChange={set("email")} placeholder={f.email} className={inputCls} />
                <input data-testid="input-province" value={form.province} onChange={set("province")} placeholder={f.province} className={inputCls} />
              </div>
              <select data-testid="input-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} ${form.budget ? "text-[#FAFAF7]" : "text-white/45"}`}>
                <option value="" className="text-black">{f.budget}</option>
                {t.contact.budgetOptions.map((o) => (
                  <option key={o} value={o} className="text-black">{o}</option>
                ))}
              </select>
              <textarea data-testid="input-message" rows={3} value={form.message} onChange={set("message")} placeholder={f.message} className={`${inputCls} resize-none`} />

              <label className="flex items-start gap-3 cursor-pointer text-sm text-white/60">
                <input
                  data-testid="input-privacy"
                  type="checkbox"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#8B7355]"
                />
                <span>{t.contact.privacy}</span>
              </label>

              <button
                data-testid="contact-submit"
                type="submit"
                className="group inline-flex items-center gap-2 bg-[#FAFAF7] text-[#1A1A1A] px-9 py-4 text-sm hover:bg-[#8B7355] hover:text-white transition-colors duration-300"
              >
                {t.contact.submit}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
